import _ from 'lodash';
import axios from 'axios';
import { combineReducers } from 'redux';
import { startSubmit, stopSubmit } from 'redux-form';
import { push } from 'connected-react-router';
import i18next from 'i18next';

import createNamedWrapperReducer from 'src/reducers/createNamedWrapperReducer';
import { listReducer, itemReducer } from 'src/reducers/common';
import { fetchList, fetchListSuccess, fetchListFailure, getList, getListPageCount } from 'src/reducers/common/listReducer';
import { fetchItem, fetchItemSuccess, fetchItemFailure, getItem, getItemExtra, setItemExtra } from 'src/reducers/common/itemReducer';
import { setOptions } from 'src/reducers/selectOptions';
import { setGlobalErrors, setGlobalSuccesses } from 'src/reducers/global';
import { redirectTo } from 'src/utils/Http';
import {
  INCOME_TAX_DECLARATION_LIST,
  INCOME_TAX_DECLARATION_BATCH_DESTROY,
  INCOME_TAX_DECLARATION_DOWNLOAD_EXCEL,
  INCOME_TAX_DECLARATION_DOWNLOAD_XML,
  INCOME_TAX_DECLARATION_SHOW,
  INCOME_TAX_DECLARATION_APPENDIX,
  INCOME_TAX_DECLARATION_RESET,
  INCOME_TAX_DECLARATION_CREATE,
  INCOME_TAX_DECLARATION_EXISTS,
  INCOME_TAX_DECLARATION_UNCONFIRMED_PAYROLLS
} from 'src/constants/EndpointUrls';

export const REDUCER_NAME = 'incomeTaxDeclarations';
export const CREATE_FORM_NAME = 'createIncomeTaxDeclarationForm';

const TOGGLE_CHECK_DECLARATION = `${REDUCER_NAME}/TOGGLE_CHECK_DECLARATION`;
const TOGGLE_CHECK_ALL_DECLARATIONS = `${REDUCER_NAME}/TOGGLE_CHECK_ALL_DECLARATIONS`;
const CLEAR_CHECKED_DECLARATIONS = `${REDUCER_NAME}/CLEAR_CHECKED_DECLARATIONS`;

export const toggleCheckDeclaration = declaration => ({
  type: TOGGLE_CHECK_DECLARATION,
  payload: declaration
});

export const toggleCheckAllDeclarations = declarations => ({
  type: TOGGLE_CHECK_ALL_DECLARATIONS,
  payload: declarations
});

export const clearCheckedDeclarations = () => ({
  type: CLEAR_CHECKED_DECLARATIONS
});

// Selectors
export const getIncomeTaxDeclarationList = state => getList(REDUCER_NAME, state);
export const getIncomeTaxDeclarationListPageCount = state => getListPageCount(REDUCER_NAME, state);
export const getCheckedDeclarations = state => _.get(state, [REDUCER_NAME, 'checkedDeclarations'], []);
export const getIsCreating = state => _.get(getItemExtra(REDUCER_NAME, state) || {}, 'isCreating', false);

export const getDeclaration = state => getItem(REDUCER_NAME, state);
export const getBranchAllocations = state => _.get(getItemExtra(REDUCER_NAME, state) || {}, 'branchAllocations', []);
export const getIsLoading = state => _.get(getItemExtra(REDUCER_NAME, state) || {}, 'isLoading', false);
export const getIsLoadingAppendix = state => _.get(getItemExtra(REDUCER_NAME, state) || {}, 'isLoadingAppendix', false);
export const getIsSaving = state => _.get(getItemExtra(REDUCER_NAME, state) || {}, 'isSaving', false);
export const getSaveErrors = state => _.get(getItemExtra(REDUCER_NAME, state) || {}, 'saveErrors', []);
export const getSaveErrorFields = state => _.get(getItemExtra(REDUCER_NAME, state) || {}, 'saveErrorFields', []);
export const getAppendixSaveErrors = state => _.get(getItemExtra(REDUCER_NAME, state) || {}, 'appendixSaveErrors', []);
export const getAppendixSaveErrorFields = state => _.get(getItemExtra(REDUCER_NAME, state) || {}, 'appendixSaveErrorFields', []);
export const getSelectOptions = state => _.get(getItemExtra(REDUCER_NAME, state) || {}, 'selectOptions', {});

export const clearSaveErrors = () => dispatch => {
  dispatch(setItemExtra(REDUCER_NAME, { saveErrors: [], saveErrorFields: [] }));
};

export const clearAppendixSaveErrors = () => dispatch => {
  dispatch(setItemExtra(REDUCER_NAME, { appendixSaveErrors: [], appendixSaveErrorFields: [] }));
};

export const fetchAppendix = id => async dispatch => {
  try {
    dispatch(setItemExtra(REDUCER_NAME, { isLoadingAppendix: true }));

    const url = INCOME_TAX_DECLARATION_APPENDIX.replace(':id', id);
    const response = await axios.get(url);
    const responsePayload = _.get(response, ['data', 'payload'], null);

    if (responsePayload) {
      const declaration = _.get(responsePayload, 'declaration', {});
      const branchAllocations = _.get(responsePayload, 'branchAllocations', []);
      const selectOptions = _.get(responsePayload, 'selectOptions', {});

      dispatch(fetchItemSuccess(REDUCER_NAME, declaration));
      dispatch(setItemExtra(REDUCER_NAME, { branchAllocations, selectOptions, isLoadingAppendix: false }));

      if (selectOptions.provinces || selectOptions.subdistricts || selectOptions.regionalTaxOffices || selectOptions.taxOffices) {
        dispatch(
          setOptions({
            provinces: selectOptions.provinces,
            subdistricts: selectOptions.subdistricts,
            regionalTaxOffices: selectOptions.regionalTaxOffices,
            taxOffices: selectOptions.taxOffices
          })
        );
      }
    } else {
      dispatch(fetchItemFailure(REDUCER_NAME, ['Appendix data not found']));
      dispatch(setItemExtra(REDUCER_NAME, { isLoadingAppendix: false }));
    }
  } catch (exception) {
    dispatch(setItemExtra(REDUCER_NAME, { isLoadingAppendix: false }));
    const errorMessages = _.get(exception, ['response', 'data', 'errors'], ['Failed to load appendix']);
    dispatch(fetchItemFailure(REDUCER_NAME, Array.isArray(errorMessages) ? errorMessages : [errorMessages]));
  }
};

export const changeTab = (id, tab) => (dispatch, getState) => {
  if (tab === 'appendix') {
    dispatch(push(INCOME_TAX_DECLARATION_APPENDIX.replace(':id', id)));
    const extra = getItemExtra(REDUCER_NAME, getState()) || {};
    if (!extra.branchAllocations || extra.branchAllocations.length === 0) {
      dispatch(fetchAppendix(id));
    }
  } else {
    dispatch(push(INCOME_TAX_DECLARATION_SHOW.replace(':id', id)));
  }
};

export const fetchIncomeTaxDeclarationList = (queries = {}) => async dispatch => {
  try {
    dispatch(fetchList(REDUCER_NAME));
    const response = await axios.get(INCOME_TAX_DECLARATION_LIST, { params: queries });

    const responsePayload = _.get(response, ['data', 'payload'], {});
    const { totalCount = 0, displayFrom = 0, displayTo = 0, totalPages = 0 } = responsePayload;
    const declarations = _.get(responsePayload, 'declarations', []);
    const selectOptions = _.get(responsePayload, 'select_options', {});

    const payload = {
      data: declarations,
      pageCount: totalPages,
      totalCount,
      displayFrom,
      displayTo,
      extras: {
        select_options: selectOptions
      }
    };

    dispatch(fetchListSuccess(REDUCER_NAME, payload));

    if (!_.isEmpty(selectOptions)) {
      dispatch(setOptions(selectOptions));
    }
  } catch (exception) {
    dispatch(fetchListFailure(REDUCER_NAME, _.get(exception, ['response', 'data', 'errors', 'messages'])));
  }
};

export const downloadDeclaration = (id, format, declarationName) => async dispatch => {
  try {
    const url = format === 'excel' ? INCOME_TAX_DECLARATION_DOWNLOAD_EXCEL.replace(':id', id) : INCOME_TAX_DECLARATION_DOWNLOAD_XML.replace(':id', id);
    redirectTo(url);

    dispatch(setGlobalSuccesses(i18next.t('features.ReportIncomeTax.show.download_modal.download_success', { name: declarationName })));
    return true;
  } catch (exception) {
    const errorMessages = _.get(exception, ['response', 'data', 'errors', 'messages'], ['Failed to download declaration']);
    dispatch(setGlobalErrors(errorMessages));
    return false;
  }
};

export const deleteCheckedDeclarations = ({ checkedDeclarations, changeToShowMode, hideModal }) => async dispatch => {
  try {
    const ids = checkedDeclarations.map(declaration => declaration.id);
    await axios.post(INCOME_TAX_DECLARATION_BATCH_DESTROY, { ids });

    dispatch(clearCheckedDeclarations());

    if (hideModal) hideModal();
    if (changeToShowMode) changeToShowMode();

    dispatch(fetchIncomeTaxDeclarationList());
  } catch (exception) {
    const errorMessages = _.get(exception, ['response', 'data', 'errors', 'messages'], ['Failed to delete declarations']);
    dispatch(setGlobalErrors(errorMessages));
    throw exception;
  }
};

export const fetchDeclaration = id => async dispatch => {
  try {
    dispatch(fetchItem(REDUCER_NAME));
    dispatch(setItemExtra(REDUCER_NAME, { isLoading: true }));

    const url = INCOME_TAX_DECLARATION_SHOW.replace(':id', id);
    const response = await axios.get(url);
    const responsePayload = _.get(response, ['data', 'payload'], null);

    if (responsePayload) {
      const declaration = _.get(responsePayload, 'declaration', {});
      const selectOptions = _.get(responsePayload, 'selectOptions', {});

      dispatch(fetchItemSuccess(REDUCER_NAME, declaration));
      dispatch(setItemExtra(REDUCER_NAME, { selectOptions, isLoading: false }));
    } else {
      dispatch(fetchItemFailure(REDUCER_NAME, ['Declaration not found']));
      dispatch(setItemExtra(REDUCER_NAME, { isLoading: false }));
    }
  } catch (exception) {
    dispatch(setItemExtra(REDUCER_NAME, { isLoading: false }));
    const errorMessages = _.get(exception, ['response', 'data', 'errors'], ['Failed to load declaration']);
    dispatch(fetchItemFailure(REDUCER_NAME, Array.isArray(errorMessages) ? errorMessages : [errorMessages]));
  }
};

export const updateDeclaration = (id, params) => async dispatch => {
  try {
    dispatch(setItemExtra(REDUCER_NAME, { isSaving: true }));
    const url = INCOME_TAX_DECLARATION_SHOW.replace(':id', id);
    const defaultTransform = axios.defaults.transformRequest[0];
    const response = await axios.put(url, params, {
      transformRequest: [defaultTransform, data => data]
    });
    const responsePayload = _.get(response, ['data', 'payload'], null);

    if (responsePayload) {
      const declaration = _.get(responsePayload, 'declaration', {});
      dispatch(fetchItemSuccess(REDUCER_NAME, declaration));
      dispatch(setItemExtra(REDUCER_NAME, { saveErrors: [], saveErrorFields: [] }));
      dispatch(setGlobalSuccesses(i18next.t('features.ReportIncomeTax.show.update_success')));
      return true;
    }

    dispatch(setItemExtra(REDUCER_NAME, { saveErrors: ['Failed to update declaration'], saveErrorFields: [] }));
    return false;
  } catch (exception) {
    const errorMessages = _.get(
      exception,
      ['response', 'data', 'errors', 'messages'],
      _.get(exception, ['response', 'data', 'errors'], ['Failed to update declaration'])
    );
    const errorFields = _.get(exception, ['response', 'data', 'errors', 'errorFields'], []);
    dispatch(
      setItemExtra(REDUCER_NAME, {
        saveErrors: Array.isArray(errorMessages) ? errorMessages : [errorMessages],
        saveErrorFields: errorFields
      })
    );
    return false;
  } finally {
    dispatch(setItemExtra(REDUCER_NAME, { isSaving: false }));
  }
};

export const updateAppendix = (id, params) => async dispatch => {
  try {
    dispatch(setItemExtra(REDUCER_NAME, { isSaving: true }));
    const url = INCOME_TAX_DECLARATION_APPENDIX.replace(':id', id);
    const defaultTransform = axios.defaults.transformRequest[0];
    const response = await axios.put(url, params, {
      transformRequest: [defaultTransform, data => data]
    });
    const responsePayload = _.get(response, ['data', 'payload'], null);

    if (responsePayload) {
      const declaration = _.get(responsePayload, 'declaration', {});
      const branchAllocations = _.get(responsePayload, 'branchAllocations', []);
      dispatch(fetchItemSuccess(REDUCER_NAME, declaration));
      dispatch(setItemExtra(REDUCER_NAME, { branchAllocations, appendixSaveErrors: [], appendixSaveErrorFields: [] }));
      dispatch(setGlobalSuccesses(i18next.t('features.ReportIncomeTax.show.update_success')));
      return true;
    }

    dispatch(setItemExtra(REDUCER_NAME, { appendixSaveErrors: ['Failed to update appendix'], appendixSaveErrorFields: [] }));
    return false;
  } catch (exception) {
    const errorMessages = _.get(
      exception,
      ['response', 'data', 'errors', 'messages'],
      _.get(exception, ['response', 'data', 'errors'], ['Failed to update appendix'])
    );
    const errorFields = _.get(exception, ['response', 'data', 'errors', 'errorFields'], []);
    dispatch(
      setItemExtra(REDUCER_NAME, {
        appendixSaveErrors: Array.isArray(errorMessages) ? errorMessages : [errorMessages],
        appendixSaveErrorFields: errorFields
      })
    );
    return false;
  } finally {
    dispatch(setItemExtra(REDUCER_NAME, { isSaving: false }));
  }
};

export const resetDeclaration = id => async dispatch => {
  try {
    dispatch(setItemExtra(REDUCER_NAME, { isSaving: true }));
    const url = INCOME_TAX_DECLARATION_RESET.replace(':id', id);
    const response = await axios.put(url);
    const responsePayload = _.get(response, ['data', 'payload'], null);

    if (responsePayload) {
      const declaration = _.get(responsePayload, 'declaration', {});
      dispatch(fetchItemSuccess(REDUCER_NAME, declaration));
      dispatch(setItemExtra(REDUCER_NAME, { saveErrors: [], saveErrorFields: [] }));
      dispatch(setGlobalSuccesses(i18next.t('features.ReportIncomeTax.show.reset_success')));
      return { ok: true };
    }

    return { error: 'Failed to reset declaration' };
  } catch (exception) {
    const errorMessages = _.get(
      exception,
      ['response', 'data', 'errors', 'messages'],
      _.get(exception, ['response', 'data', 'errors'], ['Failed to reset declaration'])
    );
    return { error: Array.isArray(errorMessages) ? errorMessages[0] : errorMessages };
  } finally {
    dispatch(setItemExtra(REDUCER_NAME, { isSaving: false }));
  }
};

export const checkIncomeTaxDeclarationExists = formValues => async dispatch => {
  try {
    const isMonthly = formValues.periodType === 'monthly';
    const params = {
      client_branch_id: formValues.branchId,
      company_id: formValues.companyId,
      period_type: formValues.periodType,
      period_year: formValues.year,
      period_month: isMonthly ? formValues.periodMonth : null,
      period_quarter: isMonthly ? null : formValues.periodQuarter
    };

    const response = await axios.get(INCOME_TAX_DECLARATION_EXISTS, { params });
    const responseData = _.get(response, 'data', {});
    return Boolean(_.get(responseData, 'isExists', false));
  } catch (exception) {
    dispatch(setGlobalErrors(_.get(exception, ['response', 'data', 'errors'])));
    return false;
  }
};

export const checkIncomeTaxDeclarationUnconfirmedPayrolls = formValues => async dispatch => {
  try {
    const isMonthly = formValues.periodType === 'monthly';
    const params = {
      client_branch_id: formValues.branchId,
      company_id: formValues.companyId,
      period_type: formValues.periodType,
      period_year: formValues.year,
      period_month: isMonthly ? formValues.periodMonth : null,
      period_quarter: isMonthly ? null : formValues.periodQuarter
    };

    const response = await axios.get(INCOME_TAX_DECLARATION_UNCONFIRMED_PAYROLLS, { params });
    const responseData = _.get(response, 'data', {});
    return _.get(responseData, 'unconfirmedPaymentMonths', []);
  } catch (exception) {
    dispatch(setGlobalErrors(_.get(exception, ['response', 'data', 'errors'])));
    return [];
  }
};

// Create declaration
export const setCreatingStatus = isCreating => dispatch => {
  dispatch(setItemExtra(REDUCER_NAME, { isCreating }));
};

export const createIncomeTaxDeclaration = formValues => async dispatch => {
  try {
    dispatch(startSubmit(CREATE_FORM_NAME));

    const isMonthly = formValues.periodType === 'monthly';
    const params = {
      clientBranchId: formValues.branchId,
      companyId: formValues.companyId,
      periodType: formValues.periodType,
      periodYear: formValues.year,
      periodMonth: isMonthly ? formValues.periodMonth : null,
      periodQuarter: isMonthly ? null : formValues.periodQuarter,
      declarationName: formValues.declarationName,
      includeAppendix051: formValues.includeAppendix051
    };

    const response = await axios.post(INCOME_TAX_DECLARATION_CREATE, params);
    const responseData = _.get(response, 'data', {});

    dispatch(stopSubmit(CREATE_FORM_NAME));

    if (responseData.success) {
      dispatch(setGlobalSuccesses(i18next.t('features.ReportIncomeTax.create.success_message')));
      dispatch(push(INCOME_TAX_DECLARATION_LIST));
      return null;
    }

    return _.get(responseData, 'errors');
  } catch (exception) {
    dispatch(stopSubmit(CREATE_FORM_NAME));
    return _.get(exception, ['response', 'data', 'errors']);
  }
};

const checkedDeclarationsReducer = (state = [], action) => {
  switch (action.type) {
    case TOGGLE_CHECK_DECLARATION: {
      const exists = state.find(d => d.id === action.payload.id);
      if (exists) return state.filter(d => d.id !== action.payload.id);
      return [...state, action.payload];
    }
    case TOGGLE_CHECK_ALL_DECLARATIONS: {
      const currentPageIds = new Set(action.payload.map(d => d.id));
      const currentPageChecked = action.payload.filter(d => state.some(s => s.id === d.id));

      if (currentPageChecked.length === action.payload.length) {
        return state.filter(d => !currentPageIds.has(d.id));
      }

      const existingOtherPages = state.filter(d => !currentPageIds.has(d.id));
      return [...existingOtherPages, ...action.payload];
    }
    case CLEAR_CHECKED_DECLARATIONS:
      return [];
    default:
      return state;
  }
};

// Main reducer
export default combineReducers({
  list: createNamedWrapperReducer(listReducer, REDUCER_NAME),
  item: createNamedWrapperReducer(itemReducer, REDUCER_NAME),
  checkedDeclarations: checkedDeclarationsReducer
});
